import { q } from 'groqd'
import _get from 'lodash/get'
import { useNextSanityImage } from 'next-sanity-image'
import Image from 'next/image'

import { IMAGE } from '@nxt-insurance/ui-kit/assets/images/index.web'
import UiKitIcon from '@nxt-insurance/ui-kit/components/Icon/index'
import { SHAPES } from '@nxt-insurance/ui-kit/components/Icon/shapes'
import { icon } from '@nxt-insurance/ui-kit/components/Icon/styles'
import type { NativeProps } from '@nxt-insurance/ui-kit/components/Icon/types'
import { COLOR, ICON_SIZE } from '@nxt-insurance/ui-kit/constants'

import { runQuery, sanityClientLike } from '@/cms/client'
import type { IconType } from '@/cms/helperTypes'
import { imageAsset } from '@/cms/helperTypes'
import { useQuery } from '@tanstack/react-query'

type Icons = typeof IMAGE.icons

const isIconOfSize = <TSize extends keyof Icons>(
  name: any,
  size: TSize
): name is keyof Icons[TSize] => Boolean(_get(IMAGE.icons[size], name))

const iconQuery = q('*')
  .filter('_type == "icon" && _id == $id')
  .grab({
    _type: q.literal('icon'),
    name: ['name.current', q.string()],
    iconSize: q.union([q.literal('small'), q.literal('medium'), q.literal('large')]),
    asset: q('asset').grab({
      _type: q.literal('image'),
      asset: q('asset').deref().grab(imageAsset),
    }),
  })

const BackgroundShapeContainer = (
  props: Pick<NativeProps, 'bgColor' | 'bgShape'> & { className?: string }
) => {
  const { bgShapeContainer } = icon()
  if (!props.bgShape || !props.bgColor) {
    return null
  }
  return (
    <svg className={bgShapeContainer({ className: props.className })} viewBox="0 0 64 64">
      <path d={SHAPES[props.bgShape]} fill={COLOR[props.bgColor]} />
    </svg>
  )
}

export const Icon = (props: { icon: Partial<IconType>; size: keyof typeof ICON_SIZE }) => {
  const { data: iconAsset } = useQuery({
    queryKey: ['icon', props.icon?.iconAsset?._ref],
    queryFn: async () => runQuery(iconQuery, { id: props.icon.iconAsset?._ref }),
    enabled: props.icon?.iconAsset?._ref !== undefined,
  })
  const imageProps = useNextSanityImage(sanityClientLike, iconAsset ?? null, {
    imageBuilder: (imageUrlBuilder) =>
      imageUrlBuilder.width(ICON_SIZE[props.size]).height(ICON_SIZE[props.size]),
  })
  if (!props.icon) {
    return null
  }
  const { name } = props.icon
  if (!name || !isIconOfSize(name, props.size)) {
    return null
  }
  const imgPath = (_get(IMAGE.icons[props.size], name) as any).default.src

  return (
    <div className="relative flex shrink-0 flex-col items-center justify-center">
      {imageProps !== null ? (
        <>
          <BackgroundShapeContainer
            bgShape={props.icon.bgShape ?? undefined}
            bgColor={props.icon.bgColor ?? undefined}
          />
          <Image className="z-[1]" {...imageProps} aria-hidden alt="" />
        </>
      ) : (
        <UiKitIcon {...props.icon} imgSrc={imgPath} />
      )}
    </div>
  )
}
